import { useEffect } from 'react';
// @mui
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
// Luxon
import { DateTime } from 'luxon';
// components
import { useDispatch, useSelector } from '../../../redux/store';
//
import { useLocales } from '../../../locales';
import { getEstimatedMemberChurnReport } from '../../../redux/slices/insight';
import Chart from '../../../components/chart/chart';
import { useChart } from '../../../components/chart';
import { fNumber } from '../../../utils/format-number';
import { IInsightTableFilters } from '../../../types/insight';

// ----------------------------------------------------------------------

export default function EstimatedMemberChurnReportView({
  fromDate,
  toDate,
  membershipTypeIds,
  minimumLengthOfMembershipInDays,
}: IInsightTableFilters) {
  const { t } = useLocales();

  const dispatch = useDispatch();

  const report = useSelector((state) => state.insight.estimatedMemberChurnReport);

  useEffect(() => {
    dispatch(
      getEstimatedMemberChurnReport({
        fromDate,
        toDate,
        membershipTypeIds,
        minimumLengthOfMembershipInDays,
      })
    );
  }, [dispatch, fromDate, toDate, membershipTypeIds, minimumLengthOfMembershipInDays]);

  const chart = {
    categories: report?.churnRates?.map((x) => x.month!) ?? [],
    series: [
      {
        name: t('Churn'),
        data: report?.churnRates?.map((x) => Math.max(x.count ?? 0, 0)) ?? [],
      },
    ],
  };

  const chartOptions = useChart({
    legend: {
      position: 'top',
      horizontalAlign: 'right',
    },
    xaxis: {
      labels: {
        formatter: (value) => DateTime.fromISO(value).toFormat('MMM yyyy'),
        rotate: -45,
        rotateAlways: true,
        trim: false,
      },
      categories: chart.categories,
      title: {
        text: t('Month'),
      },
    },
    dataLabels: {
      formatter: (val, opts) => fNumber(val as number),
      enabled: false,
    },
    yaxis: {
      labels: {
        formatter: (value) => `${fNumber(value)}%`,
      },
      title: {
        text: t('Percentage'),
      },
    },
  });

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <CardHeader
        title={t('Estimated Churn rate')}
        subheader={t(
          'Shows the estimated churn rate in percentage per month for future months, based on a minimum of 5 months prior trends . The median and average churn rates are shown for the entire period.'
        )}
      />
      <CardContent>
        <Chart dir="ltr" type="area" series={chart.series} options={chartOptions} />
      </CardContent>
    </Card>
  );
}
